import { ChecklistItemOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { ChecklistItemAnswerModel } from 'shared/domain/template/inspectionTemplateModel';
import { getActiveChecklistAnswers } from 'shared/domain/template/checklistAnswerGuards';
import {
  findProtocolAnswerByChecklistAnswerId,
  hasAnswerValue,
  ProtocolAnswer,
} from 'shared/domain/protocolAnswer';
import { ProtocolOnView } from 'presentation/inspection/inspectionOnView';
import { InspectionDataEstimateConfig } from './buildInspectionPageEstimateInput';

export type AnswerConfigState = {
  config: ChecklistItemAnswerModel;
  answer: ProtocolAnswer | undefined;
};

export type SectionScore = {
  passed: number;
  notPassed: number;
};

function aggregateComplianceResult(
  results: ComplianceAnswerOption[]
): ComplianceAnswerOption {
  const severity: Record<ComplianceAnswerOption, number> = {
    [ComplianceAnswerOption.notPassed]: 3,
    [ComplianceAnswerOption.notApplicable]: 2,
    [ComplianceAnswerOption.passed]: 1,
    [ComplianceAnswerOption.unset]: 0,
  };

  return results.reduce(
    (worst, result) => (severity[result] > severity[worst] ? result : worst),
    ComplianceAnswerOption.unset
  );
}

function getComplianceAnswerResult(
  answer: ProtocolAnswer | undefined
): ComplianceAnswerOption {
  if (!answer || answer.type !== AnswerType.Compliance) {
    return ComplianceAnswerOption.unset;
  }
  return answer.selectedOption ?? ComplianceAnswerOption.unset;
}

function getAnswerConfigStates(
  checklistItem: ChecklistItemOnView,
  protocolItem: ProtocolOnView
): AnswerConfigState[] {
  const protocolAnswerByChecklistAnswerId = new Map(
    (protocolItem.answers ?? []).map((answer) => [
      answer.checklistAnswerId,
      answer,
    ])
  );

  return getActiveChecklistAnswers(checklistItem.answers).map((config) => ({
    config,
    answer: protocolAnswerByChecklistAnswerId.get(config._id),
  }));
}

function hasFilledValue(answer: ProtocolAnswer | undefined): boolean {
  return answer != null && hasAnswerValue(answer);
}

function isUnanswered(
  checklistItem: ChecklistItemOnView,
  protocolItem: ProtocolOnView
): boolean {
  const states = getAnswerConfigStates(checklistItem, protocolItem);
  return states.length > 0 && states.every((state) => !hasFilledValue(state.answer));
}

function legacyComplianceResult(
  protocolItem: ProtocolOnView
): ComplianceAnswerOption | undefined {
  const result = protocolItem.complianceCheck?.result;
  if (result == null || result === ComplianceAnswerOption.unset) {
    return undefined;
  }
  return result;
}

export function classifyProtocolItem(
  protocolItem: ProtocolOnView
): ComplianceAnswerOption {
  const checklistItem = protocolItem.templateChecklistItem;
  const complianceStates = getAnswerConfigStates(checklistItem, protocolItem).filter(
    (state) => state.config.type === AnswerType.Compliance
  );

  if (complianceStates.length === 0) {
    return legacyComplianceResult(protocolItem) ?? ComplianceAnswerOption.unset;
  }

  return aggregateComplianceResult(
    complianceStates.map((state) => getComplianceAnswerResult(state.answer))
  );
}

export function isVisibleInChecklist(
  protocolItem: ProtocolOnView,
  inspectionData: InspectionDataEstimateConfig
): boolean {
  const result = classifyProtocolItem(protocolItem);
  if (result === ComplianceAnswerOption.notApplicable) {
    return inspectionData.showNotApplicable;
  }
  if (result === ComplianceAnswerOption.unset) {
    return isUnanswered(protocolItem.templateChecklistItem, protocolItem)
      ? inspectionData.showUnchecked
      : true;
  }
  return true;
}

export function sectionScore(
  protocolItems: ProtocolOnView[]
): SectionScore {
  const results = protocolItems.map((protocolItem) =>
    classifyProtocolItem(protocolItem)
  );
  return {
    passed: results.filter((result) => result === ComplianceAnswerOption.passed)
      .length,
    notPassed: results.filter(
      (result) => result === ComplianceAnswerOption.notPassed
    ).length,
  };
}

export function getActiveAnswerPairs(
  checklistItem: ChecklistItemOnView,
  protocolItem: ProtocolOnView
): AnswerConfigState[] {
  return getAnswerConfigStates(checklistItem, protocolItem).filter(
    (state) => state.answer != null
  );
}
